import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { fetchEmailLogs } from "../../services/apiService";
import TablePagination from '../reused-components/TablePagination';
import { usePersistedPagination } from '../../hooks/usePersistedPagination';

function EmailLogsTable({ paginationPerPage = 10, userId = null }) {
    const [data, setData] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [fetching, setFetching] = useState(false);
    const storageKey = `email-logs-${userId || 'default'}`;
    const {
        currentPage,
        perPage,
        setCurrentPage,
        setPerPage,
        resetToFirstPage,
        paginationDefaultPage,
    } = usePersistedPagination(storageKey, paginationPerPage);

    useEffect(() => {
        const fetchData = async () => {
            setFetching(true);
            try {
                const responseData = await fetchEmailLogs(userId, currentPage, perPage);
                setData(responseData.data || []);
                setTotalRows(responseData.total || 0);
            } catch (error) {
                console.error('Error fetching email logs:', error);
            } finally {
                setFetching(false);
            }
        };

        if (userId) {
            fetchData();
        }
    }, [userId, currentPage, perPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (newPerPage) => {
        setPerPage(newPerPage);
        resetToFirstPage();
    };

    const columns = [
        {
            name: 'Message',
            selector: row => row.subject,
            sortable: true,
        },
        {
            name: 'From',
            selector: row => row.from,
            width: '250px',
            sortable: true,
        },
        {
            name: 'Last event',
            selector: row => row.sent_at,
            sortable: true,
            width: '200px',
            cell: row => new Date(row.sent_at).toLocaleString(),
        },
        {
            name: 'Opens',
            selector: row => row.opens,
            width: '200px',
            sortable: true,
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            width: '200px',
            cell: row => {
                let badgeClass = '';
                switch (row.status) {
                    case 'sent':
                        badgeClass = 'green-badge';
                        break;
                    case 'bounced':
                        badgeClass = 'red-badge';
                        break;
                    case 'deferred':
                        badgeClass = 'orange-badge';
                        break;
                    default:
                        badgeClass = 'default-badge';
                }
                return <span className={`badge ${badgeClass}`}>{row.status}</span>;
            },
        },
    ];

    return (
        <DataTable
            columns={columns}
            data={data}
            progressPending={fetching}
            progressComponent={(
                <div className="spinner-container">
                    <div className="spinner"></div>
                </div>
            )}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={perPage}
            paginationDefaultPage={paginationDefaultPage}
            paginationComponent={TablePagination}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPerPageChange}
            highlightOnHover
            responsive
            className="custom-data-table"
        />
    );
}

export default EmailLogsTable; 